// BillingPage.jsx
// Settings -> Billing. Lists cards read live from Stripe (GET
// /api/billing/payment-methods) — a card saved elsewhere shows up here on
// the very next load, no local cache to keep in sync. Update and Remove
// both happen right here, inline — no separate "Add Card" page. At least
// one payment method must stay on file, so Remove is disabled on the last
// remaining card.
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, MapPin } from "lucide-react";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { stripePromise } from "../config/stripe";
import API_BASE from "../config/api";
import AppButton from "../components/AppButton";
import BottomTabBar from "../components/BottomTabBar";
import UsageReport from "../components/UsageReport";

const screenShell =
  "min-h-[100dvh] bg-white w-full min-w-0 overflow-x-hidden flex flex-col pb-24 sm:pb-28";
const contentWrap =
  "w-full max-w-lg sm:max-w-xl md:max-w-2xl mx-auto flex-1 flex flex-col min-w-0";
const sectionCard =
  "mx-4 sm:mx-5 mb-2 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden";

const CARD_BRAND_LABELS = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "American Express",
  discover: "Discover",
};

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: "15px",
      color: "#0f172a",
      "::placeholder": { color: "#94a3b8" },
    },
    invalid: { color: "#dc2626" },
  },
};

const BillingPageContent = () => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const customerId = localStorage.getItem("customer_id");

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [pendingId, setPendingId] = useState(null); // id currently being removed/defaulted

  const [editingId, setEditingId] = useState(null); // id currently showing the inline update form
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState("");

  // Billing address — the same Customer-level address Stripe uses for tax
  // on future invoices (see stripeService.updateCustomerBillingAddress).
  const [billingAddress, setBillingAddress] = useState(null); // { name, address } | null
  const [addressLoading, setAddressLoading] = useState(true);
  const [addressError, setAddressError] = useState("");
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [addressForm, setAddressForm] = useState({
    name: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "",
  });
  const [savingAddress, setSavingAddress] = useState(false);
  const [addressSaveError, setAddressSaveError] = useState("");

  const fetchPaymentMethods = useCallback(async () => {
    if (!customerId) {
      setError("User not found.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(
        `${API_BASE}/billing/payment-methods?customerId=${encodeURIComponent(customerId)}`,
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Could not load your saved cards.");
      }
      setPaymentMethods(data.paymentMethods || []);
    } catch (err) {
      setError(err.message || "Could not load your saved cards.");
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchPaymentMethods();
  }, [fetchPaymentMethods]);

  const fetchBillingAddress = useCallback(async () => {
    if (!customerId) {
      setAddressLoading(false);
      return;
    }
    setAddressLoading(true);
    setAddressError("");
    try {
      const res = await fetch(
        `${API_BASE}/billing/address?customerId=${encodeURIComponent(customerId)}`,
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Could not load your billing address.");
      }
      setBillingAddress({ name: data.name || "", address: data.address || null });
    } catch (err) {
      setAddressError(err.message || "Could not load your billing address.");
    } finally {
      setAddressLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchBillingAddress();
  }, [fetchBillingAddress]);

  const openAddressEditor = () => {
    setAddressSaveError("");
    const current = billingAddress?.address || {};
    setAddressForm({
      name: billingAddress?.name || "",
      line1: current.line1 || "",
      line2: current.line2 || "",
      city: current.city || "",
      state: current.state || "",
      postal_code: current.postal_code || "",
      country: current.country || "",
    });
    setIsEditingAddress(true);
  };

  const closeAddressEditor = () => {
    setIsEditingAddress(false);
    setAddressSaveError("");
  };

  const handleAddressFieldChange = (field) => (e) => {
    setAddressForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    if (!customerId) return;

    setAddressSaveError("");
    setSavingAddress(true);
    try {
      const res = await fetch(`${API_BASE}/billing/address`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customerId,
          name: addressForm.name,
          address: {
            line1: addressForm.line1,
            line2: addressForm.line2 || undefined,
            city: addressForm.city,
            state: addressForm.state || undefined,
            postal_code: addressForm.postal_code,
            country: addressForm.country,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Could not save your billing address.");
      }
      setBillingAddress({ name: data.name || "", address: data.address || null });
      setIsEditingAddress(false);
    } catch (err) {
      setAddressSaveError(err.message || "Could not save your billing address.");
    } finally {
      setSavingAddress(false);
    }
  };

  const handleMakeDefault = async (paymentMethodId) => {
    if (!customerId) return;
    setActionError("");
    setPendingId(paymentMethodId);
    try {
      const res = await fetch(
        `${API_BASE}/billing/payment-methods/${encodeURIComponent(paymentMethodId)}/default`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ customerId }),
        },
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Could not update your default card.");
      }
      setPaymentMethods(data.paymentMethods || []);
    } catch (err) {
      setActionError(err.message || "Could not update your default card.");
    } finally {
      setPendingId(null);
    }
  };

  const handleRemove = async (paymentMethodId) => {
    if (!customerId) return;
    if (paymentMethods.length <= 1) {
      setActionError(
        "You must keep at least one payment method on file. Update this card instead of removing it.",
      );
      return;
    }
    if (!window.confirm("Remove this card from your account?")) return;

    setActionError("");
    setPendingId(paymentMethodId);
    try {
      const res = await fetch(
        `${API_BASE}/billing/payment-methods/${encodeURIComponent(paymentMethodId)}?customerId=${encodeURIComponent(customerId)}`,
        { method: "DELETE" },
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Could not remove that card.");
      }
      setPaymentMethods(data.paymentMethods || []);
    } catch (err) {
      setActionError(err.message || "Could not remove that card.");
    } finally {
      setPendingId(null);
    }
  };

  const openEditor = (paymentMethodId) => {
    setActionError("");
    setUpdateError("");
    setEditingId(paymentMethodId);
  };

  const closeEditor = () => {
    setEditingId(null);
    setUpdateError("");
  };

  const handleUpdateSubmit = async (oldId, e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setUpdateError("");
    setUpdating(true);
    try {
      const setupRes = await fetch(`${API_BASE}/billing/payment-methods/setup-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customerId }),
      });
      const setupData = await setupRes.json();
      if (!setupRes.ok || !setupData.clientSecret) {
        throw new Error(setupData.message || "Could not start card update. Please try again.");
      }

      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
        setupData.clientSecret,
        { payment_method: { card: elements.getElement(CardElement) } },
      );
      if (stripeError) {
        throw new Error(stripeError.message || "Your card could not be verified. Please try again.");
      }

      const replaceRes = await fetch(
        `${API_BASE}/billing/payment-methods/${encodeURIComponent(oldId)}/replace`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ customerId, paymentMethodId: setupIntent.payment_method }),
        },
      );
      const replaceData = await replaceRes.json();
      if (!replaceRes.ok || !replaceData.success) {
        throw new Error(replaceData.message || "Could not save the new card. Please try again.");
      }

      setPaymentMethods(replaceData.paymentMethods || []);
      setEditingId(null);
    } catch (err) {
      setUpdateError(err.message || "Could not update the card. Please try again.");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className={screenShell}>
      <div className={contentWrap}>
        <div className="px-4 sm:px-5 pt-8 sm:pt-10 pb-4">
          <button
            type="button"
            onClick={() => navigate("/user-settings")}
            className="text-sm text-gray-500 flex items-center gap-1 mb-4 py-1 touch-manipulation"
          >
            ← Back
          </button>
          <h1 className="text-xl sm:text-2xl font-extrabold text-gray-900">
            Billing
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
            Saved payment methods
          </p>
        </div>

        <div className="flex-1 min-w-0 pb-2">
          {loading && (
            <p className="px-4 sm:px-5 text-sm text-gray-500">Loading…</p>
          )}

          {!loading && error && (
            <div className="px-4 sm:px-5">
              <p className="text-sm text-red-500 mb-3">{error}</p>
              <AppButton variant="primary" onClick={fetchPaymentMethods} className="mb-4">
                Try Again
              </AppButton>
            </div>
          )}

          {!loading && !error && actionError && (
            <div className="px-4 sm:px-5 mb-2">
              <p className="text-sm text-red-500">{actionError}</p>
            </div>
          )}

          {!loading && !error && paymentMethods.length === 0 && (
            <div className="px-4 sm:px-5 pb-2">
              <p className="text-sm text-gray-500 mb-4">
                No payment method on file yet.
              </p>
            </div>
          )}

          {!loading && !error && paymentMethods.length > 0 && (
            <div className={sectionCard}>
              {paymentMethods.map((pm) => {
                const isPending = pendingId === pm.id;
                const isOnly = paymentMethods.length <= 1;
                const isEditing = editingId === pm.id;

                return (
                  <div
                    key={pm.id}
                    className="w-full px-4 sm:px-5 py-3.5 sm:py-4 border-b border-gray-100 last:border-b-0"
                  >
                    <div className="flex items-center justify-between min-h-[32px]">
                      <div className="flex items-center gap-3 min-w-0">
                        <CreditCard size={18} className="text-blue-500 flex-shrink-0" />
                        <div className="min-w-0 text-left">
                          <span className="text-sm sm:text-base font-medium text-gray-900 block">
                            {CARD_BRAND_LABELS[pm.brand] || pm.brand || "Card"} •••• {pm.last4 || "----"}
                          </span>
                          <span className="text-xs text-gray-400">
                            Expires {String(pm.expMonth || "--").padStart(2, "0")}/{pm.expYear || "----"}
                          </span>
                        </div>
                      </div>
                      {pm.isDefault && (
                        <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded-full flex-shrink-0">
                          Default
                        </span>
                      )}
                    </div>

                    {!isEditing && (
                      <div className="flex items-center gap-4 mt-2.5 pl-[30px]">
                        <button
                          type="button"
                          disabled={isPending}
                          onClick={() => openEditor(pm.id)}
                          className="text-xs font-semibold text-blue-600 touch-manipulation disabled:opacity-50"
                        >
                          Update
                        </button>
                        {!pm.isDefault && (
                          <button
                            type="button"
                            disabled={isPending}
                            onClick={() => handleMakeDefault(pm.id)}
                            className="text-xs font-semibold text-gray-500 touch-manipulation disabled:opacity-50"
                          >
                            {isPending ? "Working…" : "Make default"}
                          </button>
                        )}
                        <button
                          type="button"
                          disabled={isPending || isOnly}
                          onClick={() => handleRemove(pm.id)}
                          title={isOnly ? "At least one payment method must stay on file" : undefined}
                          className="text-xs font-semibold text-red-500 touch-manipulation disabled:opacity-40"
                        >
                          {isPending ? "Working…" : "Remove"}
                        </button>
                      </div>
                    )}

                    {isEditing && (
                      <form
                        onSubmit={(e) => handleUpdateSubmit(pm.id, e)}
                        className="mt-3 pl-[30px]"
                      >
                        <div
                          className="border border-blue-100 bg-blue-50/40 rounded-xl px-3.5 py-3 mb-2"
                        >
                          <CardElement options={CARD_ELEMENT_OPTIONS} />
                        </div>

                        {updateError && (
                          <p className="text-xs text-red-500 mb-2">{updateError}</p>
                        )}

                        <div className="flex items-center gap-3">
                          <button
                            type="submit"
                            disabled={updating || !stripe || !elements}
                            className="text-xs font-semibold text-white bg-blue-600 rounded-full px-4 py-2 touch-manipulation disabled:opacity-50"
                          >
                            {updating ? "Saving…" : "Save new card"}
                          </button>
                          <button
                            type="button"
                            disabled={updating}
                            onClick={closeEditor}
                            className="text-xs font-semibold text-gray-500 touch-manipulation disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {!loading && !error && paymentMethods.length > 0 && (
            <p className="px-4 sm:px-5 text-xs text-gray-400 mb-3">
              At least one payment method must stay on file.
            </p>
          )}

          <div className="px-4 sm:px-5 pt-4 pb-1">
            <h2 className="text-sm font-bold text-gray-900">Billing address</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Used to calculate tax on your subscription
            </p>
          </div>

          {addressLoading && (
            <p className="px-4 sm:px-5 text-sm text-gray-500">Loading…</p>
          )}

          {!addressLoading && addressError && (
            <div className="px-4 sm:px-5">
              <p className="text-sm text-red-500 mb-3">{addressError}</p>
              <AppButton variant="primary" onClick={fetchBillingAddress} className="mb-4">
                Try Again
              </AppButton>
            </div>
          )}

          {!addressLoading && !addressError && (
            <div className={sectionCard}>
              <div className="w-full px-4 sm:px-5 py-3.5 sm:py-4">
                {!isEditingAddress && (
                  <>
                    <div className="flex items-start gap-3 min-w-0">
                      <MapPin size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 text-left">
                        {billingAddress?.address ? (
                          <>
                            {billingAddress.name && (
                              <span className="text-sm sm:text-base font-medium text-gray-900 block">
                                {billingAddress.name}
                              </span>
                            )}
                            <span className="text-sm text-gray-700 block">
                              {billingAddress.address.line1}
                              {billingAddress.address.line2 ? `, ${billingAddress.address.line2}` : ""}
                            </span>
                            <span className="text-sm text-gray-700 block">
                              {[billingAddress.address.city, billingAddress.address.state, billingAddress.address.postal_code]
                                .filter(Boolean)
                                .join(", ")}
                            </span>
                            <span className="text-xs text-gray-400">
                              {billingAddress.address.country}
                            </span>
                          </>
                        ) : (
                          <span className="text-sm text-gray-500">
                            No billing address on file yet.
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-2.5 pl-[30px]">
                      <button
                        type="button"
                        onClick={openAddressEditor}
                        className="text-xs font-semibold text-blue-600 touch-manipulation"
                      >
                        {billingAddress?.address ? "Update" : "Add address"}
                      </button>
                    </div>
                  </>
                )}

                {isEditingAddress && (
                  <form onSubmit={handleAddressSubmit} className="space-y-2.5">
                    <input
                      type="text"
                      placeholder="Name on account"
                      value={addressForm.name}
                      onChange={handleAddressFieldChange("name")}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400"
                    />
                    <input
                      type="text"
                      placeholder="Address line 1"
                      required
                      value={addressForm.line1}
                      onChange={handleAddressFieldChange("line1")}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400"
                    />
                    <input
                      type="text"
                      placeholder="Address line 2 (optional)"
                      value={addressForm.line2}
                      onChange={handleAddressFieldChange("line2")}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400"
                    />
                    <div className="flex gap-2.5">
                      <input
                        type="text"
                        placeholder="City"
                        required
                        value={addressForm.city}
                        onChange={handleAddressFieldChange("city")}
                        className="w-1/2 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400"
                      />
                      <input
                        type="text"
                        placeholder="State / Province"
                        value={addressForm.state}
                        onChange={handleAddressFieldChange("state")}
                        className="w-1/2 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400"
                      />
                    </div>
                    <div className="flex gap-2.5">
                      <input
                        type="text"
                        placeholder="Postal code"
                        required
                        value={addressForm.postal_code}
                        onChange={handleAddressFieldChange("postal_code")}
                        className="w-1/2 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400"
                      />
                      <input
                        type="text"
                        placeholder="Country (e.g. US)"
                        required
                        maxLength={2}
                        value={addressForm.country}
                        onChange={handleAddressFieldChange("country")}
                        className="w-1/2 text-sm border border-gray-200 rounded-lg px-3 py-2 uppercase focus:outline-none focus:border-blue-400"
                      />
                    </div>

                    {addressSaveError && (
                      <p className="text-xs text-red-500">{addressSaveError}</p>
                    )}

                    <div className="flex items-center gap-3 pt-1">
                      <button
                        type="submit"
                        disabled={savingAddress}
                        className="text-xs font-semibold text-white bg-blue-600 rounded-full px-4 py-2 touch-manipulation disabled:opacity-50"
                      >
                        {savingAddress ? "Saving…" : "Save address"}
                      </button>
                      <button
                        type="button"
                        disabled={savingAddress}
                        onClick={closeAddressEditor}
                        className="text-xs font-semibold text-gray-500 touch-manipulation disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="px-4 sm:px-5 pt-4 pb-1">
          <h2 className="text-sm font-bold text-gray-900">Usage report</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Every AI API run with its input, output and total tokens. Filter by date and export as CSV.
          </p>
        </div>
        <div className="px-4 sm:px-5 pb-4">
          <UsageReport scope="merchant" />
        </div>
      </div>

      <BottomTabBar />
    </div>
  );
};

const BillingPage = () => (
  <Elements stripe={stripePromise}>
    <BillingPageContent />
  </Elements>
);

export default BillingPage;
